import { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  RotateCcw,
  Calendar as CalendarIcon,
  Clock3,
} from "lucide-react";
import { Mosque } from "@phosphor-icons/react";
import { savePublicCalendarContext } from "@/lib/publicCalendarContext";

const API_URL = import.meta.env.VITE_API_URL;

// Format time with prayer-aware AM/PM defaults
const formatTimeWithAMPM = (time, prayerName = null) => {
  if (!time) return "-";
  if (/[AaPp][Mm]/.test(time)) return time;

  const [hoursStr, minutesStr] = String(time).split(":");
  const hours = Number(hoursStr);
  const minutes = Number(minutesStr);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return time;

  const padMinutes = minutes.toString().padStart(2, "0");
  let displayHours = hours;
  let period;

  if (prayerName === "fajr" || prayerName === "sunrise") {
    period = "AM";
    displayHours = hours % 12 || 12;
  } else if (prayerName === "duhr" || prayerName === "jummah") {
    if (hours >= 10 && hours < 12) {
      period = "AM";
      displayHours = hours;
    } else {
      period = "PM";
      displayHours = hours > 12 ? hours - 12 : hours;
    }
  } else if (
    prayerName === "asr" ||
    prayerName === "maghrib" ||
    prayerName === "isha"
  ) {
    period = "PM";
    displayHours = hours > 12 ? hours - 12 : hours;
  } else {
    period = hours >= 12 ? "PM" : "AM";
    displayHours = hours % 12 || 12;
  }

  return `${displayHours}:${padMinutes} ${period}`;
};

const monthLabel = (key) => {
  if (!key || !/^[0-9]{4}-[0-9]{2}$/.test(key)) return key || "";
  const [y, m] = key.split("-").map(Number);
  const date = new Date(y, m - 1, 1);
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

const formatGregorianDate = (yearMonth, day) => {
  if (!yearMonth || !day) return "";
  const [y, m] = yearMonth.split("-").map(Number);
  const dNum = Number(day);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(dNum))
    return `${yearMonth}-${String(day).padStart(2, "0")}`;
  const date = new Date(y, m - 1, dNum);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

export default function MonthlyPrayerWidget() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const theme = searchParams.get("theme") || "light";
  const color = searchParams.get("color") || "bf9a2f";
  
  // Size parameters: can be pixel values or "max"
  const widthParam = searchParams.get("width") || "max";
  const heightParam = searchParams.get("height") || "max";

  const [loading, setLoading] = useState(true);
  const [monthLoading, setMonthLoading] = useState(false);
  const [error, setError] = useState(null);
  const [masjidData, setMasjidData] = useState(null);
  const [masjidId, setMasjidId] = useState(null);
  const [days, setDays] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [jummahTimes, setJummahTimes] = useState([]);
  const [khutbahTimes, setKhutbahTimes] = useState([]);
  const [isCompact, setIsCompact] = useState(false);
  const [selectedDayId, setSelectedDayId] = useState(null);

  const tableScrollRef = useRef(null);
  const todayRowRef = useRef(null);

  const currentMonthKey = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  }, []);

  const shiftMonthKey = (key, delta) => {
    if (!key || !/^\d{4}-\d{2}$/.test(key)) return null;
    const [y, m] = key.split("-").map(Number);
    const dt = new Date(y, m - 1 + delta, 1);
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    return `${yyyy}-${mm}`;
  };

  const isDark = theme === "dark";
  const accentColor = `#${color}`;
  const bgColor = isDark ? "#1a1a1a" : "#ffffff";
  const textColor = isDark ? "#ffffff" : "#0f172a";
  const mutedColor = isDark ? "#9ca3af" : "#64748b";
  const borderColor = isDark
    ? "rgba(255, 255, 255, 0.12)"
    : "rgba(0, 0, 0, 0.1)";
  const headerBg = isDark
    ? "rgba(255, 255, 255, 0.03)"
    : "rgba(0, 0, 0, 0.02)";
  const stripeBg = isDark
    ? "rgba(255, 255, 255, 0.03)"
    : "rgba(0, 0, 0, 0.025)";
  const todayBg = isDark
    ? `${accentColor}25`
    : `${accentColor}15`;

  // Detect compact layout for small screens
  useEffect(() => {
    const updateCompact = () => setIsCompact(window.innerWidth < 768);
    updateCompact();
    window.addEventListener("resize", updateCompact);
    return () => window.removeEventListener("resize", updateCompact);
  }, []);

  // Match the iframe document background to the widget background so there's no
  // visible gap below the widget when it renders shorter than the iframe height on mobile.
  useEffect(() => {
    document.documentElement.style.backgroundColor = bgColor;
    document.body.style.backgroundColor = bgColor;
    document.body.style.margin = "0";
    document.body.style.padding = "0";
  }, [bgColor]);

  // On desktop: auto-scroll to today's row when data loads or month changes
  useEffect(() => {
    if (isCompact || !tableScrollRef.current || days.length === 0) return;
    // Only auto-scroll when viewing the current month
    if (selectedMonth !== currentMonthKey) return;
    const timer = setTimeout(() => {
      if (todayRowRef.current && tableScrollRef.current) {
        const container = tableScrollRef.current;
        const row = todayRowRef.current;
        const containerTop = container.getBoundingClientRect().top;
        const rowTop = row.getBoundingClientRect().top;
        const offset = rowTop - containerTop - container.clientHeight / 2 + row.clientHeight / 2;
        container.scrollTop += offset;
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [days, isCompact, selectedMonth, currentMonthKey]);

  // Report content height to parent frame so the iframe can auto-resize
  useEffect(() => {
    const report = () => {
      const h = document.documentElement.scrollHeight;
      try { window.parent.postMessage({ type: "muslimplus-resize", height: h }, "*"); } catch (_) {}
    };
    report();
    const ro = new ResizeObserver(report);
    ro.observe(document.documentElement);
    return () => ro.disconnect();
  }, [days, selectedDayId, jummahTimes, khutbahTimes, isCompact, monthLoading]);

  // Load masjid info from widget token
  useEffect(() => {
    if (!token) return;

    const loadMasjidInfo = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/v1/widget-data/${token}`);
        if (!response.ok) throw new Error("Failed to load widget data");

        const data = await response.json();
        if (!data.ok)
          throw new Error(data.error || "Failed to load widget data");

        setMasjidData(data.masjid);
        const id = data.masjid.id || data.masjid.masjidId || data.masjid.key;
        setMasjidId(id);
        setSelectedMonth(currentMonthKey);
      } catch (err) {
        console.error("Error loading masjid info:", err);
        setError(err.message || "Failed to load masjid data");
      } finally {
        setLoading(false);
      }
    };

    loadMasjidInfo();
  }, [token, currentMonthKey]);

  // Load monthly prayer times
  useEffect(() => {
    if (!masjidId || !selectedMonth) return;

    const loadMonthData = async () => {
      try {
        setMonthLoading(true);
        const response = await fetch(
          `${API_URL}/v1/public/masjid/${masjidId}/prayer-times/${selectedMonth}`
        );

        if (!response.ok)
          throw new Error("Failed to load monthly prayer times");

        const data = await response.json();
        if (!data.ok) throw new Error("No prayer times available");

        const monthDays = Array.isArray(data.days) ? data.days.slice() : [];
        monthDays.sort((a, b) => (a.day || "").localeCompare(b.day || ""));
        setDays(monthDays);

        // Set selected day to today if available, otherwise first day
        if (monthDays.length > 0) {
          const today = new Date();
          const isoToday = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
          const match = monthDays.find((d) => `${selectedMonth}-${d.day}` === isoToday);
          setSelectedDayId((match && match.day) || monthDays[0].day);
        } else {
          setSelectedDayId(null);
        }

        // Process Jummah times
        const normalizeTime = (t) => {
          if (!t) return null;
          const timeStr = String(t).trim();
          const ampmMatch = timeStr.match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/i);
          if (!ampmMatch) return null;
          let h = parseInt(ampmMatch[1], 10);
          const m = parseInt(ampmMatch[2], 10);
          const suffix = ampmMatch[3];
          if (Number.isNaN(h) || Number.isNaN(m)) return null;
          if (suffix) {
            const isPM = suffix.toLowerCase() === "pm";
            if (isPM && h !== 12) h += 12;
            else if (!isPM && h === 12) h = 0;
          } else {
            if (!(h >= 10 && h < 12)) {
              h = (h % 12) + 12;
            }
          }
          return { raw: t, minutes: h * 60 + m };
        };

        const sortedJummah = (
          Array.isArray(data.jummahTimes) ? data.jummahTimes : []
        )
          .map(normalizeTime)
          .filter(Boolean)
          .sort((a, b) => a.minutes - b.minutes)
          .map((entry) => entry.raw);
        setJummahTimes(sortedJummah);

        // Process khutbah times
        const sortedKhutbah = (
          Array.isArray(data.khutbahTimes) ? data.khutbahTimes : []
        )
          .map(normalizeTime)
          .filter(Boolean)
          .sort((a, b) => a.minutes - b.minutes)
          .map((entry) => entry.raw);
        setKhutbahTimes(sortedKhutbah);
      } catch (err) {
        console.error("Error loading month data:", err);
        setError(err.message || "Failed to load prayer times");
        setDays([]);
      } finally {
        setMonthLoading(false);
      }
    };

    loadMonthData();
  }, [masjidId, selectedMonth]);

  // If selected day disappears after data change, reset to first
  useEffect(() => {
    if (!days || days.length === 0) {
      setSelectedDayId(null);
      return;
    }
    if (!selectedDayId || !days.find((d) => d.day === selectedDayId)) {
      setSelectedDayId(days[0].day);
    }
  }, [days, selectedDayId]);

  const onPrevMonth = () => {
    if (!selectedMonth) return;
    const prev = shiftMonthKey(selectedMonth, -1);
    if (prev) setSelectedMonth(prev);
  };

  const onNextMonth = () => {
    if (!selectedMonth) return;
    const next = shiftMonthKey(selectedMonth, 1);
    if (next) setSelectedMonth(next);
  };

  const onResetMonth = () => {
    setSelectedMonth(currentMonthKey);
  };

  const isToday = (yearMonth, day) => {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(
      today.getMonth() + 1
    ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    return `${yearMonth}-${String(day).padStart(2, "0")}` === todayStr;
  };

  // Container styles based on width/height params
  const containerStyle = {
    fontFamily: "-apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, sans-serif",
    backgroundColor: bgColor,
    color: textColor,
    width: widthParam === "max" ? "100%" : `${widthParam}px`,
    maxWidth: "100%",
    height: heightParam === "max" ? "100vh" : `${heightParam}px`,
    minHeight: "400px",
    minWidth: "320px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    borderRadius: widthParam === "max" && heightParam === "max" ? "0" : "12px",
    border: widthParam === "max" && heightParam === "max" ? "none" : (isDark
      ? "1px solid rgba(255, 255, 255, 0.12)"
      : "1px solid rgba(0, 0, 0, 0.1)"),
    boxShadow: widthParam === "max" && heightParam === "max" ? "none" : (isDark
      ? "0 10px 40px -10px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)"
      : "0 10px 40px -10px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.03)"),
  };

  if (!token) {
    return (
      <div style={{ ...containerStyle, alignItems: "center", justifyContent: "center", padding: "20px" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>🕌</div>
          <p style={{ color: "#ef4444", fontSize: "18px", fontWeight: "600", margin: 0 }}>
            Missing Widget Token
          </p>
          <p style={{ color: mutedColor, fontSize: "14px", marginTop: "8px", margin: "8px 0 0 0" }}>
            Please configure this widget from your masjid dashboard
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ ...containerStyle, alignItems: "center", justifyContent: "center", padding: "20px" }}>
        <Loader2 style={{ width: "48px", height: "48px", color: accentColor, animation: "spin 1s linear infinite" }} />
        <p style={{ marginTop: "16px", color: mutedColor, fontSize: "14px", margin: "16px 0 0 0" }}>
          Loading prayer times...
        </p>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...containerStyle, alignItems: "center", justifyContent: "center", padding: "20px", textAlign: "center" }}>
        <div style={{ width: "56px", height: "56px", borderRadius: "50%", backgroundColor: "rgba(239, 68, 68, 0.1)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
          <span style={{ fontSize: "28px" }}>⚠️</span>
        </div>
        <p style={{ color: "#ef4444", fontSize: "16px", fontWeight: "600", margin: 0 }}>{error}</p>
      </div>
    );
  }

  const current = days.find((d) => d.day === selectedDayId) || days[0];

  return (
    <div style={containerStyle}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={{
        padding: "12px 16px",
        borderBottom: `1px solid ${borderColor}`,
        background: headerBg,
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
          {/* Masjid Info */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
            <div style={{
              width: "40px", height: "40px", borderRadius: "10px",
              backgroundColor: `${accentColor}15`, display: "flex",
              alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <Mosque size={22} weight="fill" style={{ color: accentColor }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h2 style={{ margin: 0, fontSize: "16px", fontWeight: "700", color: textColor }}>
                {masjidData?.name || masjidData?.title || "Masjid"}
              </h2>
              <p style={{ margin: "2px 0 0 0", fontSize: "12px", color: mutedColor }}>
                Monthly Prayer Calendar
              </p>
            </div>
          </div>

          {/* Month Navigation */}
          <div style={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
            <a
              href={token ? `/masjids/calendar?token=${encodeURIComponent(token)}` : "/masjids/calendar"}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: "none", border: `1px solid ${borderColor}`, cursor: "pointer",
                padding: "6px", borderRadius: "6px", display: "flex", alignItems: "center",
                color: textColor, transition: "all 0.2s", textDecoration: "none",
              }}
              title="View on Muslim+"
              onClick={() => {
                const id = masjidData?.masjidId || masjidData?.id || masjidData?.key || masjidData?.masjid_id;
                if (id) {
                  savePublicCalendarContext({ masjidId: id, masjid: masjidData });
                }
              }}
            >
              <CalendarIcon style={{ width: "14px", height: "14px" }} />
            </a>
            {selectedMonth !== currentMonthKey && (
              <button onClick={onResetMonth} style={{
                background: "none", border: `1px solid ${borderColor}`, cursor: "pointer",
                padding: "6px", borderRadius: "6px", display: "flex", alignItems: "center",
                color: textColor, transition: "all 0.2s",
              }} title="Current Month">
                <RotateCcw style={{ width: "14px", height: "14px" }} />
              </button>
            )}
            <button onClick={onPrevMonth} style={{
              background: "none", border: `1px solid ${borderColor}`, cursor: "pointer",
              padding: "6px", borderRadius: "6px", display: "flex", alignItems: "center",
              color: textColor, transition: "all 0.2s",
            }}>
              <ChevronLeft style={{ width: "14px", height: "14px" }} />
            </button>
            <div style={{
              padding: "6px 12px", borderRadius: "6px", backgroundColor: `${accentColor}15`,
              fontSize: "13px", fontWeight: "600", color: textColor, minWidth: "140px", textAlign: "center",
            }}>
              {monthLabel(selectedMonth)}
            </div>
            <button onClick={onNextMonth} style={{
              background: "none", border: `1px solid ${borderColor}`, cursor: "pointer",
              padding: "6px", borderRadius: "6px", display: "flex", alignItems: "center",
              color: textColor, transition: "all 0.2s",
            }}>
              <ChevronRight style={{ width: "14px", height: "14px" }} />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: "auto", padding: "0" }}>
        {monthLoading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 20px", height: "100%" }}>
            <Loader2 style={{ width: "32px", height: "32px", color: accentColor, animation: "spin 1s linear infinite", marginRight: "12px" }} />
            <span style={{ color: mutedColor, fontSize: "14px" }}>Loading {monthLabel(selectedMonth)}...</span>
          </div>
        ) : days.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 20px", color: mutedColor, height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <CalendarIcon style={{ width: "48px", height: "48px", marginBottom: "16px", opacity: 0.3 }} />
            <p style={{ margin: 0 }}>No prayer times available for this month</p>
          </div>
        ) : isCompact ? (
          /* Mobile Compact View */
          <div style={{ padding: "12px" }}>
            {/* Day Selector */}
            <div style={{ marginBottom: "12px" }}>
              <label style={{ fontSize: "12px", color: mutedColor, marginBottom: "4px", display: "block" }}>
                Select a day
              </label>
              <select
                value={selectedDayId || ""}
                onChange={(e) => setSelectedDayId(e.target.value)}
                style={{
                  width: "100%", padding: "10px 12px", borderRadius: "8px",
                  border: `1px solid ${borderColor}`, backgroundColor: isDark ? "#2a2a2a" : "#fff",
                  color: textColor, fontSize: "14px", cursor: "pointer",
                }}
              >
                {days.map((d) => (
                  <option key={d.day} value={d.day}>
                    {formatGregorianDate(selectedMonth, d.day)}
                    {isToday(selectedMonth, d.day) ? " (Today)" : ""}
                  </option>
                ))}
              </select>
            </div>

            {/* Day Detail Card */}
            {current && (
              <div style={{
                borderRadius: "12px", border: `1px solid ${borderColor}`,
                overflow: "hidden", backgroundColor: isDark ? "#222" : "#fff",
              }}>
                {/* Islamic Date */}
                {current.islamicDate && (
                  <div style={{
                    padding: "10px 16px", textAlign: "center",
                    borderBottom: `1px solid ${borderColor}`, backgroundColor: headerBg,
                  }}>
                    <span style={{ fontSize: "13px", color: mutedColor }}>{current.islamicDate}</span>
                  </div>
                )}

                {/* Prayer Times Table */}
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
                      <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: "600", color: mutedColor }}>Prayer</th>
                      <th style={{ padding: "10px 12px", textAlign: "center", fontWeight: "600", color: mutedColor }}>Adhan</th>
                      <th style={{ padding: "10px 12px", textAlign: "center", fontWeight: "600", color: mutedColor }}>Iqama</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { key: "fajr", label: "Fajr" },
                      { key: "sunrise", label: "Sunrise", noIqama: true },
                      { key: "duhr", label: "Dhuhr" },
                      { key: "asr", label: "Asr" },
                      { key: "maghrib", label: "Maghrib" },
                      { key: "isha", label: "Isha" },
                    ].map((prayer, idx) => (
                      <tr key={prayer.key} style={{ borderTop: idx > 0 ? `1px solid ${borderColor}` : "none" }}>
                        <td style={{ padding: "10px 12px", fontWeight: "500" }}>{prayer.label}</td>
                        <td style={{ padding: "10px 12px", textAlign: "center" }}>
                          {prayer.noIqama
                            ? formatTimeWithAMPM(current[prayer.key], prayer.key)
                            : formatTimeWithAMPM(current[prayer.key]?.adhan, prayer.key)}
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "center", fontWeight: "600" }}>
                          {prayer.noIqama ? "—" : formatTimeWithAMPM(current[prayer.key]?.iqama, prayer.key)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Jummah Times */}
            {jummahTimes.length > 0 && (
              <div style={{
                marginTop: "12px", borderRadius: "12px", border: `1px solid ${borderColor}`,
                overflow: "hidden", backgroundColor: isDark ? "#222" : "#fff",
              }}>
                <div style={{
                  padding: "10px 16px", display: "flex", alignItems: "center",
                  justifyContent: "center", gap: "8px", borderBottom: `1px solid ${borderColor}`,
                  backgroundColor: headerBg,
                }}>
                  <Clock3 style={{ width: "14px", height: "14px", color: accentColor }} />
                  <span style={{ fontSize: "13px", fontWeight: "600" }}>Jummah Times</span>
                </div>
                {khutbahTimes.length > 0 ? (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
                        <th style={{ padding: "10px 12px", textAlign: "center", fontWeight: "600", color: mutedColor }}>Khutbah</th>
                        <th style={{ padding: "10px 12px", textAlign: "center", fontWeight: "600", color: mutedColor }}>Prayer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const maxRows = Math.max(jummahTimes.length, khutbahTimes.length);
                        return Array.from({ length: maxRows }, (_, idx) => (
                          <tr key={idx} style={{ borderTop: idx > 0 ? `1px solid ${borderColor}` : "none" }}>
                            <td style={{ padding: "10px 12px", textAlign: "center" }}>
                              {khutbahTimes[idx] ? formatTimeWithAMPM(khutbahTimes[idx], "jummah") : "—"}
                            </td>
                            <td style={{ padding: "10px 12px", textAlign: "center", fontWeight: "600" }}>
                              {jummahTimes[idx] ? formatTimeWithAMPM(jummahTimes[idx], "jummah") : "—"}
                            </td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "16px", padding: "12px 16px" }}>
                    {jummahTimes.map((time, idx) => (
                      <div key={idx} style={{ textAlign: "center", fontWeight: "600", fontSize: "14px" }}>
                        {formatTimeWithAMPM(time, "jummah")}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Desktop Table View - Like PublicMasjidCalendar */
          <div style={{ padding: "12px", display: "flex", flexDirection: "column", gap: "12px", height: "100%" }}>
            {/* Main Prayer Times Table */}
            <div ref={tableScrollRef} style={{ flex: 1, overflow: "auto", borderRadius: "8px", border: `1px solid ${borderColor}` }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px", minWidth: "900px", tableLayout: "fixed" }}>
                <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                  <tr style={{ backgroundColor: bgColor }}>
                    <th style={{ padding: "10px 8px", textAlign: "left", fontWeight: "700", color: mutedColor, borderBottom: `2px solid ${borderColor}`, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", width: "11%" }}>Gregorian Date</th>
                    <th style={{ padding: "10px 8px", textAlign: "left", fontWeight: "700", color: mutedColor, borderBottom: `2px solid ${borderColor}`, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", width: "11%" }}>Hijri Date</th>
                    <th style={{ padding: "10px 6px", textAlign: "center", fontWeight: "700", color: mutedColor, borderBottom: `2px solid ${borderColor}`, fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.3px", width: "7%", lineHeight: "1.3" }}>Fajr<br/>Adhan</th>
                    <th style={{ padding: "10px 6px", textAlign: "center", fontWeight: "700", color: mutedColor, borderBottom: `2px solid ${borderColor}`, fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.3px", backgroundColor: `${accentColor}10`, width: "7%", lineHeight: "1.3" }}>Fajr<br/>Iqama</th>
                    <th style={{ padding: "10px 6px", textAlign: "center", fontWeight: "700", color: mutedColor, borderBottom: `2px solid ${borderColor}`, fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.3px", width: "7%", lineHeight: "1.3" }}>Sunrise</th>
                    <th style={{ padding: "10px 6px", textAlign: "center", fontWeight: "700", color: mutedColor, borderBottom: `2px solid ${borderColor}`, fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.3px", width: "7%", lineHeight: "1.3" }}>Dhuhr<br/>Adhan</th>
                    <th style={{ padding: "10px 6px", textAlign: "center", fontWeight: "700", color: mutedColor, borderBottom: `2px solid ${borderColor}`, fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.3px", backgroundColor: `${accentColor}10`, width: "7%", lineHeight: "1.3" }}>Dhuhr<br/>Iqama</th>
                    <th style={{ padding: "10px 6px", textAlign: "center", fontWeight: "700", color: mutedColor, borderBottom: `2px solid ${borderColor}`, fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.3px", width: "7%", lineHeight: "1.3" }}>Asr<br/>Adhan</th>
                    <th style={{ padding: "10px 6px", textAlign: "center", fontWeight: "700", color: mutedColor, borderBottom: `2px solid ${borderColor}`, fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.3px", backgroundColor: `${accentColor}10`, width: "7%", lineHeight: "1.3" }}>Asr<br/>Iqama</th>
                    <th style={{ padding: "10px 6px", textAlign: "center", fontWeight: "700", color: mutedColor, borderBottom: `2px solid ${borderColor}`, fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.3px", width: "7%", lineHeight: "1.3" }}>Maghrib<br/>Adhan</th>
                    <th style={{ padding: "10px 6px", textAlign: "center", fontWeight: "700", color: mutedColor, borderBottom: `2px solid ${borderColor}`, fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.3px", backgroundColor: `${accentColor}10`, width: "7%", lineHeight: "1.3" }}>Maghrib<br/>Iqama</th>
                    <th style={{ padding: "10px 6px", textAlign: "center", fontWeight: "700", color: mutedColor, borderBottom: `2px solid ${borderColor}`, fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.3px", width: "7%", lineHeight: "1.3" }}>Isha<br/>Adhan</th>
                    <th style={{ padding: "10px 6px", textAlign: "center", fontWeight: "700", color: mutedColor, borderBottom: `2px solid ${borderColor}`, fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.3px", backgroundColor: `${accentColor}10`, width: "8%", lineHeight: "1.3" }}>Isha<br/>Iqama</th>
                  </tr>
                </thead>
                <tbody>
                  {days.map((day, idx) => {
                    const todayRow = isToday(selectedMonth, day.day);
                    const rowBg = todayRow ? todayBg : (idx % 2 === 1 ? stripeBg : "transparent");

                    return (
                      <tr key={day.day} ref={todayRow ? todayRowRef : null} style={{ backgroundColor: rowBg }}>
                        <td style={{ padding: "8px", fontWeight: todayRow ? "700" : "500", borderTop: idx > 0 ? `1px solid ${borderColor}` : "none" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            {todayRow && (
                              <div style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: accentColor, flexShrink: 0 }} />
                            )}
                            <span>{formatGregorianDate(selectedMonth, day.day)}</span>
                          </div>
                        </td>
                        <td style={{ padding: "8px", color: mutedColor, fontSize: "11px", borderTop: idx > 0 ? `1px solid ${borderColor}` : "none" }}>{day.islamicDate || "-"}</td>
                        <td style={{ padding: "8px", textAlign: "center", borderTop: idx > 0 ? `1px solid ${borderColor}` : "none" }}>{formatTimeWithAMPM(day.fajr?.adhan, "fajr")}</td>
                        <td style={{ padding: "8px", textAlign: "center", fontWeight: "600", backgroundColor: `${accentColor}08`, borderTop: idx > 0 ? `1px solid ${borderColor}` : "none" }}>{formatTimeWithAMPM(day.fajr?.iqama, "fajr")}</td>
                        <td style={{ padding: "8px", textAlign: "center", borderTop: idx > 0 ? `1px solid ${borderColor}` : "none" }}>{formatTimeWithAMPM(day.sunrise, "sunrise")}</td>
                        <td style={{ padding: "8px", textAlign: "center", borderTop: idx > 0 ? `1px solid ${borderColor}` : "none" }}>{formatTimeWithAMPM(day.duhr?.adhan, "duhr")}</td>
                        <td style={{ padding: "8px", textAlign: "center", fontWeight: "600", backgroundColor: `${accentColor}08`, borderTop: idx > 0 ? `1px solid ${borderColor}` : "none" }}>{formatTimeWithAMPM(day.duhr?.iqama, "duhr")}</td>
                        <td style={{ padding: "8px", textAlign: "center", borderTop: idx > 0 ? `1px solid ${borderColor}` : "none" }}>{formatTimeWithAMPM(day.asr?.adhan, "asr")}</td>
                        <td style={{ padding: "8px", textAlign: "center", fontWeight: "600", backgroundColor: `${accentColor}08`, borderTop: idx > 0 ? `1px solid ${borderColor}` : "none" }}>{formatTimeWithAMPM(day.asr?.iqama, "asr")}</td>
                        <td style={{ padding: "8px", textAlign: "center", borderTop: idx > 0 ? `1px solid ${borderColor}` : "none" }}>{formatTimeWithAMPM(day.maghrib?.adhan, "maghrib")}</td>
                        <td style={{ padding: "8px", textAlign: "center", fontWeight: "600", backgroundColor: `${accentColor}08`, borderTop: idx > 0 ? `1px solid ${borderColor}` : "none" }}>{formatTimeWithAMPM(day.maghrib?.iqama, "maghrib")}</td>
                        <td style={{ padding: "8px", textAlign: "center", borderTop: idx > 0 ? `1px solid ${borderColor}` : "none" }}>{formatTimeWithAMPM(day.isha?.adhan, "isha")}</td>
                        <td style={{ padding: "8px", textAlign: "center", fontWeight: "600", backgroundColor: `${accentColor}08`, borderTop: idx > 0 ? `1px solid ${borderColor}` : "none" }}>{formatTimeWithAMPM(day.isha?.iqama, "isha")}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Jummah Times */}
            {jummahTimes.length > 0 && (
              <div style={{ borderRadius: "8px", border: `1px solid ${borderColor}`, overflow: "hidden", flexShrink: 0 }}>
                <div style={{
                  padding: "10px 16px", display: "flex", alignItems: "center",
                  justifyContent: "center", gap: "8px", borderBottom: `1px solid ${borderColor}`,
                  backgroundColor: headerBg,
                }}>
                  <Clock3 style={{ width: "14px", height: "14px", color: accentColor }} />
                  <span style={{ fontSize: "13px", fontWeight: "600" }}>Jummah Times</span>
                </div>
                {khutbahTimes.length > 0 ? (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
                        <th style={{ padding: "10px 12px", textAlign: "center", fontWeight: "600", color: mutedColor }}>Khutbah</th>
                        <th style={{ padding: "10px 12px", textAlign: "center", fontWeight: "600", color: mutedColor }}>Prayer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const maxRows = Math.max(jummahTimes.length, khutbahTimes.length);
                        return Array.from({ length: maxRows }, (_, idx) => (
                          <tr key={idx} style={{ borderTop: idx > 0 ? `1px solid ${borderColor}` : "none" }}>
                            <td style={{ padding: "10px 12px", textAlign: "center" }}>
                              {khutbahTimes[idx] ? formatTimeWithAMPM(khutbahTimes[idx], "jummah") : "—"}
                            </td>
                            <td style={{ padding: "10px 12px", textAlign: "center", fontWeight: "600" }}>
                              {jummahTimes[idx] ? formatTimeWithAMPM(jummahTimes[idx], "jummah") : "—"}
                            </td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "16px", padding: "12px 16px" }}>
                    {jummahTimes.map((time, idx) => (
                      <div key={idx} style={{ textAlign: "center", fontWeight: "600", fontSize: "14px" }}>
                        {formatTimeWithAMPM(time, "jummah")}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding: "8px 16px", borderTop: `1px solid ${borderColor}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        gap: "4px", backgroundColor: headerBg, flexShrink: 0,
      }}>
        <a
          href="https://muslimplus.org"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            textDecoration: "none",
            color: "inherit",
            transition: "color 0.2s ease",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <span style={{ fontSize: "11px", color: mutedColor }}>Powered by</span>
          <span style={{ fontSize: "12px", fontWeight: "700", color: accentColor }}>Muslim+</span>
        </a>
      </div>
    </div>
  );
}
